import "./Profile.css";
import { useDispatch, useSelector } from "react-redux";
import { setUserData } from "../../../../../../../redux-store/auth/actions";
import { CircularProgress, TextField, Box, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import ContactEmergencyIcon from "@mui/icons-material/ContactEmergency";
import AccessibilityNewIcon from "@mui/icons-material/AccessibilityNew";
import ListAltIcon from "@mui/icons-material/ListAlt";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import TranslateIcon from "@mui/icons-material/Translate";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useState, useRef, useEffect } from "react";
import { motion, easeOut } from "framer-motion";
import useUpload from "./Shared/useUpload";
import backend from "../../../../../../../api";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import SkeletonLoading from "../../../../../../GlobalComponents/SkeletonLoading/SkeletonLoading";

export default function ProfileHome() {
  const { user } = useSelector((s) => s.auth);
  const dispatch = useDispatch();
  const nav = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { upload, loading: upLoading } = useUpload();
  const picInput = useRef();
  const [initialLoading, setInitialLoading] = useState(true); // New state for initial loading
  const loading = !user || !user._id;

  const [data, setData] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
  });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pwdMode, setPwdMode] = useState(false);
  const [pwd, setPwd] = useState({ old: "", new: "", confirm: "" });

  // Simulate initial loading delay to show CircularProgress
  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1000); // 1-second delay
    return () => clearTimeout(timer);
  }, []);

  const save = async () => {
    if (pwdMode && pwd.new !== pwd.confirm) {
      enqueueSnackbar("Passwords don't match", { variant: "warning" });
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...(!pwdMode ? { name: data.name, phone: data.phone } : {}),
        ...(pwdMode ? { old_pass: pwd.old, new_pass: pwd.new, confirm_pass: pwd.confirm } : {}),
      };
      const url = pwdMode ? `/api/user/changepassword/${user._id}` : `/api/user/${user._id}`;
      const res = await backend.put(url, body);
      if (!pwdMode) {
        const merged = { ...user, ...res.data.data };
        dispatch(setUserData(merged));
      }
      enqueueSnackbar("Updated!", { variant: "success" });
      setEditing(false);
      setPwdMode(false);
      setPwd({ old: "", new: "", confirm: "" });
    } catch (e) {
      enqueueSnackbar(e.response?.data?.message || "Error", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const pickPic = () => picInput.current.click();
  const onPic = async (e) => {
    try {
      const url = await upload(e.target.files);
      if (!url) return;
      const res = await backend.put(`/api/user/${user._id}`, { imageURL: url });
      dispatch(setUserData({ ...user, ...res.data.data }));
    } catch {
      /* handled in hook */
    }
  };

  const rows = [
    { icon: MailOutlineIcon, label: user?.email || "No email provided" },
    { icon: PhoneIcon, label: user?.phone || "No phone provided" },
    { icon: ContactEmergencyIcon, label: "Emergency Contacts", path: "contacts" },
    { icon: AccessibilityNewIcon, label: "Special needs", path: "special-needs" },
  ];

  const navRows = [
    { icon: ListAltIcon, label: "Bookings", path: "#" },
    { icon: TravelExploreIcon, label: "Travel itineraries", path: "itineraries" },
    { icon: TranslateIcon, label: "Change language", path: "language" },
    { icon: SupportAgentIcon, label: "Customer support", path: "support" },
  ];

  return (
    <motion.div
      className="pf-wrap"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: easeOut }}
    >
      <div className="pf-hero">
        {initialLoading || loading ? (
          <Box
            sx={{
              width: "100%",
              height: "160px",
              borderRadius: "12px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "#E5E7EB",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "160px",
              borderRadius: "12px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "#E5E7EB",
            }}
          >
              <img className="pf-hero-img" src="/assets/images/profile_hero.png" alt="Header image" />
           
          </Box>
        )}

        <div className="pf-av-wrap">
          {initialLoading || loading ? (
            <Box
              sx={{
                width: "96px",
                height: "96px",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#E5E7EB",
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box
              sx={{
                width: "96px",
                height: "96px",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#E5E7EB",
              }}
            >
              {user?.imageURL ? (
                <img className="pf-avatar" src={user.imageURL} alt="User avatar" />
              ) : (
                <Typography>Image Not Found</Typography>
              )}
            </Box>
          )}

          <button className="pf-av-edit" onClick={pickPic} disabled={loading || upLoading}>
            {upLoading ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : (
              <EditIcon sx={{ fontSize: 16 }} />
            )}
          </button>
          <input hidden ref={picInput} type="file" accept="image/*" onChange={onPic} />
        </div>
      </div>

      <div className="pf-card">
        {initialLoading || loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "1.6rem" }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <h3 className="pf-name">{user?.name || "Add name"}</h3>
        )}

        {(initialLoading || loading) ? (
          <>
            <SkeletonLoading height="44px" width="100%" br="12px" padding="0" />
            <SkeletonLoading height="44px" width="100%" br="12px" padding="0" />
            <SkeletonLoading height="44px" width="100%" br="12px" padding="0" />
          </>
        ) : (
          <Box sx={{ position: "relative" }}>
            {saving && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                  zIndex: 10,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}
            {!pwdMode &&
              rows.map((r) => {
                const Icon = r.icon;
                const isCTA = r.path === "contacts" || r.path === "special-needs";
                if (isCTA) {
                  return (
                    <button
                      key={r.label}
                      type="button"
                      onClick={() => nav(r.path)}
                      aria-label={r.label}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                        marginTop: 10,
                        padding: "12px 14px",
                        borderRadius: 12,
                        border: "1px solid #E5E7EB",
                        background: "#F8FAFC",
                        color: "#111827",
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: "pointer",
                        outline: "none",
                      }}
                    >
                      <Icon sx={{ fontSize: 18 }} />
                      <span>{r.label}</span>
                    </button>
                  );
                }
                return (
                  <div key={r.label} className="pf-row" onClick={() => r.path && nav(r.path)}>
                    <Icon sx={{ fontSize: 18 }} />
                    {r.label}
                  </div>
                );
              })}
          </Box>
        )}

        {!(initialLoading || loading) && editing && !pwdMode && (
          <>
            <TextField
              size="small"
              label="Full name"
              sx={{ mt: 1 }}
              fullWidth
              value={data.name}
              onChange={(e) => setData({ ...data, name: e.target.value })}
            />
            <TextField
              size="small"
              label="Phone"
              sx={{ mt: 2 }}
              fullWidth
              value={data.phone}
              onChange={(e) => setData({ ...data, phone: e.target.value })}
            />
          </>
        )}

        {!(initialLoading || loading) && pwdMode && (
          <>
            {["old", "new", "confirm"].map((k) => (
              <TextField
                key={k}
                type="password"
                size="small"
                sx={{ mt: 2 }}
                fullWidth
                label={k === "old" ? "Current Password" : k === "new" ? "New Password" : "Confirm Password"}
                value={pwd[k]}
                onChange={(e) => setPwd({ ...pwd, [k]: e.target.value })}
              />
            ))}
          </>
        )}

        {!(initialLoading || loading) && !editing && !pwdMode && (
          <button className="pf-edit" onClick={() => setEditing(true)}>
            Edit
          </button>
        )}
        {!(initialLoading || loading) && !pwdMode && (
          <button className="pf-edit" style={{ marginTop: 12 }} onClick={() => setPwdMode(true)}>
            Change Password
          </button>
        )}

        {!(initialLoading || loading) && (editing || pwdMode) && (
          <button className="pf-save" disabled={saving} onClick={save}>
            {saving ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Save"}
          </button>
        )}
      </div>

      {(initialLoading || loading) ? (
        <div className="pf-list" style={{ padding: "0 0.75rem" }}>
          <SkeletonLoading height="36px" width="100%" br="10px" padding="0" />
          <SkeletonLoading height="36px" width="100%" br="10px" padding="0" />
          <SkeletonLoading height="36px" width="100%" br="10px" padding="0" />
          <SkeletonLoading height="36px" width="100%" br="10px" padding="0" />
        </div>
      ) : (
        <ul className="pf-list">
          {navRows.map((r) => {
            const Icon = r.icon;
            return (
              <li key={r.label} onClick={() => nav(r.path)}>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Icon sx={{ fontSize: 18 }} />
                  {r.label}
                </span>
                <ChevronRightIcon sx={{ fontSize: 18 }} />
              </li>
            );
          })}
        </ul>
      )}
    </motion.div>
  );
}